import { useEffect, useState } from "react";
import { GetGroupResponse, GroupStatus } from "../../../types";
import { ApiError, GroupApi } from "../api";
import { relativeTime } from "../format";
import { href, navigate } from "../route";
import { CaptureRow } from "./CaptureRow";
import { LevelBadge } from "./LevelBadge";
import { Button, Panel, inputClass } from "./Shell";

export const GroupDetail = ({ id }: { id: string }) => {
  const [group, setGroup] = useState<GetGroupResponse | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [mergeTarget, setMergeTarget] = useState("");

  const load = async () => {
    try {
      const g = await GroupApi.get(id);
      setGroup(g);
      setTitle(g.title);
      setNote(g.note ?? "");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Failed to load");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Request failed");
    } finally {
      setBusy(false);
    }
  };

  if (error && !group) return <p className="text-sm text-red-600">{error}</p>;
  if (!group) return <p className="text-sm text-neutral-500">Loading…</p>;
  const dirty = title !== group.title || note !== (group.note ?? "");

  return (
    <div className="space-y-3">
      <Panel className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs text-neutral-500">
          <LevelBadge level={group.level} />
          <span className="font-mono">{group.source}</span>
          <span className="rounded bg-neutral-100 dark:bg-neutral-800 px-1">{group.env}</span>
          <span>first {relativeTime(group.firstSeenAt)}</span>
          <span>last {relativeTime(group.latestSeenAt)}</span>
          <span className="flex-1" />
          <span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{group.totalCount}</span>
          <span>events{group.mergedGroupIds.length ? ` (${group.count} own + ${group.mergedGroupIds.length} merged groups)` : ""}</span>
        </div>
        {(group.droppedCount ?? 0) > 0 && (
          <div className="text-sm rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2">
            <span className="font-semibold">{group.droppedCount}</span> of these hits were counted but <span className="font-semibold">not stored</span>: the group went over the flood limit (see Settings). Captures kept during the flood are marked as samples.
          </div>
        )}
        {group.mergedInto && (
          <div className="text-sm rounded bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900 p-2 flex items-center gap-2">
            <span>This group is merged into <a className="underline" href={href.group(group.mergedInto)}>{group.mergedInto}</a>.</span>
            <span className="flex-1" />
            <Button disabled={busy} onClick={() => act(() => GroupApi.update({ id, mergedInto: null }))}>Un-merge</Button>
          </div>
        )}
        <input className={`${inputClass} w-full text-base font-semibold`} value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea className={`${inputClass} w-full`} rows={2} placeholder="Notes…" value={note} onChange={(e) => setNote(e.target.value)} />
        <div className="flex flex-wrap gap-2 items-center">
          <Button kind="primary" disabled={busy || !dirty} onClick={() => act(() => GroupApi.update({ id, title, note }))}>Save</Button>
          <select className={inputClass} value={group.status} disabled={busy} onChange={(e) => act(() => GroupApi.update({ id, status: e.target.value as GroupStatus }))}>
            <option value="open">open</option>
            <option value="resolved">resolved</option>
            <option value="ignored">ignored</option>
          </select>
          {!group.mergedInto && (
            <>
              <input className={`${inputClass} w-72`} placeholder="merge into group id…" value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} />
              <Button disabled={busy || !mergeTarget.trim()} onClick={() => act(() => GroupApi.update({ id, mergedInto: mergeTarget.trim() })).then(() => setMergeTarget(""))}>Merge</Button>
            </>
          )}
          <span className="flex-1" />
          <Button disabled={busy} onClick={() => act(() => GroupApi.update({ id, recount: true }))} >Recount</Button>
          <Button kind="danger" disabled={busy} onClick={() => { if (confirm("Delete this group record? Its captures are kept.")) act(() => GroupApi.delete(id)).then(() => navigate(href.groups())); }}>Delete group</Button>
        </div>
        <div className="text-xs text-neutral-400 font-mono">id {group.id}</div>
        {group.mergedGroupIds.length > 0 && (
          <div className="text-xs text-neutral-500">
            Merged in: {group.mergedGroupIds.map((g) => <a key={g} className="underline mr-2" href={href.group(g)}>{g.slice(0, 8)}</a>)}
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </Panel>
      <Panel>
        <div className="px-3 py-2 text-xs text-neutral-500 border-b border-neutral-100 dark:border-neutral-800">Captures</div>
        {group.captures.items.map((c) => (
          <CaptureRow key={c.id} capture={c} />
        ))}
        {group.captures.items.length === 0 && <p className="p-4 text-sm text-neutral-500">No captures (yet, or they expired).</p>}
        {group.captures.cursor && (
          <div className="p-2 text-xs text-neutral-500">
            Showing the newest page. <a className="underline" href={href.captures({ groupId: group.id })}>See all in the capture list</a>.
          </div>
        )}
      </Panel>
    </div>
  );
};
